/**
 * ==========================================================================
 * First Order Popup — controller
 * ==========================================================================
 * Shows a one-time "welcome offer" popup when a visitor arrives via the
 * /firstorder entry point (see first-order-redirect.js + the
 * page.first-order-handler.liquid template for how that URL is captured
 * without losing the visitor's original page).
 *
 * Requires: snippets/first-order-popup.liquid (markup) + first-order-popup.css
 * No dependencies. No global pollution — everything is wrapped in an IIFE.
 * ==========================================================================
 */

(function () {
  "use strict";

  /* ------------------------------------------------------------------ *
   * 1. CONFIGURATION — edit these values only
   * ------------------------------------------------------------------ */
  const OFFER_PATH = "/firstorder"; // The public entry URL customers click/share
  const MYSTERY_VARIANT_ID = 53900175278420; // ⚠ REPLACE with your real mystery-can variant ID (Product > Variant > check the URL: /variants/XXXXXXXXX)
  const DISCOUNT_CODE = "WELCOMECA15"; // Leave as "" to skip auto-applying a code
  const LOCAL_STORAGE_KEY = "firstOrderClaimed"; // localStorage flag, once claimed
  const CART_ATTRIBUTE = "first_order_offer"; // Cart attribute set on claim
  const UNLOCK_PARAM = "first_order_unlock"; // Query param set by the redirect handler

  /* ------------------------------------------------------------------ *
   * 2. INTERNAL STATE / DOM REFERENCES
   * ------------------------------------------------------------------ */
  let overlayEl = null;
  let claimBtnEl = null;
  let closeBtnEl = null;
  let errorEl = null;
  let lastFocusedEl = null;

  /* ------------------------------------------------------------------ *
   * 3. HELPERS
   * ------------------------------------------------------------------ */

  /** Has the visitor already claimed the offer? */
  function hasClaimedOffer() {
    try {
      return window.localStorage.getItem(LOCAL_STORAGE_KEY) === "true";
    } catch (err) {
      // If localStorage is unavailable (privacy mode, disabled, etc.)
      // fail safe by treating the offer as already claimed so we never
      // show a popup we can't reliably suppress next time.
      console.warn("[first-order-popup] localStorage unavailable:", err);
      return true;
    }
  }

  /** Persist the "claimed" flag. */
  function markOfferClaimed() {
    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    } catch (err) {
      console.warn("[first-order-popup] could not write to localStorage:", err);
    }
  }

  /** Did this pageview arrive via the /firstorder redirect handler? */
  function arrivedViaOfferLink() {
    const params = new URLSearchParams(window.location.search);
    return params.has(UNLOCK_PARAM);
  }

  /** Strip the unlock query param from the URL without adding history entries. */
  function cleanUnlockParamFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete(UNLOCK_PARAM);
    const cleaned =
      url.pathname + (url.search ? url.search : "") + url.hash;
    window.history.replaceState({}, document.title, cleaned);
  }

  /** Show an inline error message inside the popup. */
  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = "";
  }

  /** Toggle the claim button's loading state. */
  function setClaimLoading(isLoading) {
    if (!claimBtnEl) return;
    claimBtnEl.disabled = isLoading;
    claimBtnEl.setAttribute("data-loading", isLoading ? "true" : "false");
  }

  /* ------------------------------------------------------------------ *
   * 4. POPUP OPEN / CLOSE
   * ------------------------------------------------------------------ */

  function openPopup() {
    if (!overlayEl) return;
    lastFocusedEl = document.activeElement;
    overlayEl.hidden = false;
    // Force a reflow so the CSS transition runs (removes + re-adds state)
    requestAnimationFrame(() => {
      overlayEl.setAttribute("data-state", "open");
    });
    overlayEl.setAttribute("aria-hidden", "false");
    document.addEventListener("keydown", handleKeydown);
    document.body.style.overflow = "hidden";

    if (claimBtnEl) claimBtnEl.focus();
  }

  function closePopup() {
    if (!overlayEl) return;
    overlayEl.setAttribute("data-state", "closed");
    overlayEl.setAttribute("aria-hidden", "true");
    document.removeEventListener("keydown", handleKeydown);
    document.body.style.overflow = "";

    // Wait for the fade-out transition before fully hiding it from the a11y tree.
    window.setTimeout(() => {
      if (overlayEl && overlayEl.getAttribute("data-state") !== "open") {
        overlayEl.hidden = true;
      }
    }, 300);

    if (lastFocusedEl && typeof lastFocusedEl.focus === "function") {
      lastFocusedEl.focus();
    }
  }

  function handleKeydown(event) {
    if (event.key === "Escape") {
      closePopup();
    }
  }

  function handleOverlayClick(event) {
    // Only close when the click landed on the overlay itself, not the card.
    if (event.target === overlayEl) {
      closePopup();
    }
  }

  /* ------------------------------------------------------------------ *
   * 5. SHOPIFY CART OPERATIONS (Ajax Cart API)
   * ------------------------------------------------------------------ */

  /** Add the free mystery can to the cart. */
  async function addMysteryCanToCart() {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: MYSTERY_VARIANT_ID, quantity: 1 }],
      }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(
        errorBody.description || "Could not add the mystery can to your cart."
      );
    }

    return response.json();
  }

  /** Tag the cart with an attribute so it's identifiable in Shopify admin/order. */
  async function setCartAttribute() {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        attributes: { [CART_ATTRIBUTE]: "true" },
      }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(
        errorBody.description || "Could not update the cart attributes."
      );
    }

    return response.json();
  }

  /**
   * Build the Shopify discount activation URL. Visiting this URL applies the
   * code to the session and redirects back to `returnTo` — no manual code
   * entry required, no Shopify Plus / Checkout Extensions needed.
   */
  function buildDiscountRedirectUrl(returnTo) {
    const encodedReturn = encodeURIComponent(returnTo);
    return `/discount/${encodeURIComponent(DISCOUNT_CODE)}?redirect=${encodedReturn}`;
  }

  /* ------------------------------------------------------------------ *
   * 6. CLAIM FLOW
   * ------------------------------------------------------------------ */

  async function handleClaimClick() {
    clearError();
    setClaimLoading(true);

    try {
      // 1. Add the mystery can via the Ajax Cart API.
      await addMysteryCanToCart();

      // 2. Tag the cart so the order is identifiable later.
      await setCartAttribute();

      // 3. Persist "claimed" so the popup never appears again on this browser.
      markOfferClaimed();

      // 4. Apply the discount code automatically, if one is configured.
      //    This performs a full navigation (Shopify's discount endpoint),
      //    so it must be the last step. The cart persists across it.
      if (DISCOUNT_CODE) {
        const returnTo =
          window.location.pathname + window.location.search + window.location.hash;
        window.location.href = buildDiscountRedirectUrl(returnTo);
        return; // Navigation is about to happen; nothing else to do.
      }

      // No discount code configured — just close the popup, cart already updated.
      closePopup();
    } catch (err) {
      console.error("[first-order-popup] claim failed:", err);
      showError(
        "Something went wrong applying your offer. Please refresh and try again."
      );
      setClaimLoading(false);
    }
  }

  /* ------------------------------------------------------------------ *
   * 7. INITIALIZATION
   * ------------------------------------------------------------------ */

  function cacheDomReferences() {
    overlayEl = document.getElementById("first-order-popup");
    claimBtnEl = document.getElementById("fop-claim-btn");
    closeBtnEl = document.getElementById("fop-close-btn");
    errorEl = document.getElementById("fop-error");
  }

  function bindEvents() {
    if (claimBtnEl) claimBtnEl.addEventListener("click", handleClaimClick);
    if (closeBtnEl) closeBtnEl.addEventListener("click", closePopup);
    if (overlayEl) overlayEl.addEventListener("click", handleOverlayClick);
  }

  function init() {
    cacheDomReferences();

    if (!overlayEl) {
      // Markup wasn't rendered on this page for some reason — nothing to do.
      return;
    }

    // Trigger condition: arrived via /firstorder AND not already claimed.
    if (!arrivedViaOfferLink()) return;
    if (hasClaimedOffer()) return;

    bindEvents();
    openPopup();

    // Remove the tracking param so refreshes/shares don't re-trigger it
    // and the URL stays clean for the visitor.
    cleanUnlockParamFromUrl();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();